import type { DealParticipant, PmpDealType } from '@openx/types/deal';
import type { TargetingState } from '@openx/types/targeting/targeting';
import { useEffect, useRef } from 'react';
import { useDealContext } from '../../../utils/context/DealContext';

type UseUpdateForecastState = {
  disableButton: boolean;
  observableFormItems: {
    observablePackage: string | null;
    observablePmpDealType: PmpDealType | null;
    observableTargeting: TargetingState | null;
    observableDealParticipants: DealParticipant[];
  };
};

export const useUpdateForecastState = ({
  disableButton,
  observableFormItems: { observablePackage, observablePmpDealType, observableTargeting, observableDealParticipants },
}: UseUpdateForecastState) => {
  const { useDealForecast } = useDealContext();
  const {
    forecastState: { job_id },
    actions: { setGenerateForecastButtonDisabled, resetForecast },
  } = useDealForecast();
  const prevTargetingRef = useRef(observableTargeting);
  const prevPackageRef = useRef(observablePackage);
  const prevPmpDealTypeRef = useRef(observablePmpDealType);
  const prevDealParticipantsRef = useRef(observableDealParticipants);

  // biome-ignore lint/correctness/useExhaustiveDependencies: needed here?
  useEffect(() => {
    setGenerateForecastButtonDisabled(!!disableButton || !!job_id);
  }, [disableButton]);

  useEffect(() => {
    const shouldResetForecast =
      !disableButton &&
      ((prevTargetingRef.current !== observableTargeting && observableTargeting !== null) ||
        (prevPackageRef.current !== observablePackage && observablePackage !== null) ||
        (prevDealParticipantsRef.current !== observableDealParticipants && observableDealParticipants.length > 0));

    if (shouldResetForecast) {
      resetForecast();
      setGenerateForecastButtonDisabled(false);
      prevTargetingRef.current = observableTargeting;
      prevPackageRef.current = observablePackage;
      prevDealParticipantsRef.current = observableDealParticipants;
    }

    if (prevPmpDealTypeRef.current !== observablePmpDealType) {
      resetForecast();
      prevPmpDealTypeRef.current = observablePmpDealType;
    }
  }, [
    disableButton,
    setGenerateForecastButtonDisabled,
    observableDealParticipants,
    observablePackage,
    observablePmpDealType,
    observableTargeting,
    resetForecast,
  ]);
};
